/**
 * Lovasok Marketplace API Utility
 *
 * Centralized API communication layer for all Alpine.js components.
 * Handles REST API calls, authentication, and error handling.
 *
 * @package Lovasok_Marketplace
 */

export class LovasokAPI {
    static getParams() {
        return window.lovasokParams || {};
    }

    /**
     * GET request to WordPress REST API
     *
     * @param {string} endpoint - API endpoint (e.g., 'horses', 'messages')
     * @param {Object} params - Query parameters
     * @param {Object} options - Optional fetch options ({ signal })
     * @returns {Promise<Object>} - API response data
     */
    static async get(endpoint, params = {}, options = {}) {
        const apiParams = this.getParams();
        const url = new URL(`${apiParams.apiUrl}${endpoint}`, window.location.origin);

        // Build query string
        Object.entries(params).forEach(([key, value]) => {
            if (value !== null && value !== undefined && value !== '') {
                url.searchParams.append(key, value);
            }
        });

        try {
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'X-WP-Nonce': apiParams.nonce || '',
                    'Content-Type': 'application/json'
                },
                credentials: 'same-origin',
                signal: options.signal || undefined,
            });

            if (!response.ok) {
                const body = await this.parseResponse(response);
                const message = body?.message || `API Error: ${response.status} ${response.statusText}`;
                throw new Error(message);
            }

            return await response.json();

        } catch (error) {
            if (error?.name === 'AbortError') {
                throw error;
            }
            console.error(`API GET Error [${endpoint}]:`, error);
            throw error;
        }
    }

    /**
     * POST request to WordPress REST API
     *
     * @param {string} endpoint - API endpoint
     * @param {Object} data - Request body data
     * @returns {Promise<Object>} - API response data
     */
    static async post(endpoint, data = {}) {
        const apiParams = this.getParams();
        try {
            const response = await fetch(`${apiParams.apiUrl}${endpoint}`, {
                method: 'POST',
                headers: {
                    'X-WP-Nonce': apiParams.nonce || '',
                    'Content-Type': 'application/json'
                },
                credentials: 'same-origin',
                body: JSON.stringify(data)
            });

            if (!response.ok) {
                const body = await this.parseResponse(response);
                const message = body?.message || `API Error: ${response.status} ${response.statusText}`;
                throw new Error(message);
            }

            return await response.json();

        } catch (error) {
            console.error(`API POST Error [${endpoint}]:`, error);
            throw error;
        }
    }

    /**
     * PUT request to WordPress REST API
     *
     * @param {string} endpoint - API endpoint
     * @param {Object} data - Request body data
     * @returns {Promise<Object>} - API response data
     */
    static async put(endpoint, data = {}) {
        const apiParams = this.getParams();
        try {
            const response = await fetch(`${apiParams.apiUrl}${endpoint}`, {
                method: 'PUT',
                headers: {
                    'X-WP-Nonce': apiParams.nonce || '',
                    'Content-Type': 'application/json'
                },
                credentials: 'same-origin',
                body: JSON.stringify(data)
            });

            if (!response.ok) {
                const body = await this.parseResponse(response);
                const message = body?.message || `API Error: ${response.status} ${response.statusText}`;
                throw new Error(message);
            }

            return await response.json();

        } catch (error) {
            console.error(`API PUT Error [${endpoint}]:`, error);
            throw error;
        }
    }

    /**
     * DELETE request to WordPress REST API
     *
     * @param {string} endpoint - API endpoint
     * @returns {Promise<Object>} - API response data
     */
    static async delete(endpoint) {
        const apiParams = this.getParams();
        try {
            const response = await fetch(`${apiParams.apiUrl}${endpoint}`, {
                method: 'DELETE',
                headers: {
                    'X-WP-Nonce': apiParams.nonce || '',
                    'Content-Type': 'application/json'
                },
                credentials: 'same-origin',
            });

            if (!response.ok) {
                const body = await this.parseResponse(response);
                const message = body?.message || `API Error: ${response.status} ${response.statusText}`;
                throw new Error(message);
            }

            return await response.json();

        } catch (error) {
            console.error(`API DELETE Error [${endpoint}]:`, error);
            throw error;
        }
    }

    /**
     * Parse response with fallback for malformed JSON
     *
     * @param {Response} response - Fetch response object
     * @returns {Promise<Object>} - Parsed JSON or empty object
     */
    static async parseResponse(response) {
        const text = await response.text();
        try {
            return JSON.parse(text);
        } catch (e) {
            console.warn('Failed to parse JSON response:', text);
            return {};
        }
    }
}
